import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { FloatLabel } from 'primereact/floatlabel';
import { Toast } from 'primereact/toast';
import { Password } from 'primereact/password';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import bgImage from '../assets/images/new_bgs.png';
import logoImage from '../assets/images/payPlus/payPlusNoBackground.png';
import logoImageWithTitle from '../assets/images/payPlus/payPlusWithTitle.png';
import axiosInstance from '../utils/axiosInstance';

const ResetPasswordConfirm = () => {
    const { id: userId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const toast = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    const validationSchema = Yup.object({
        newPassword: Yup.string()
            .min(8, t('passwordMinLength'))
            .matches(/[a-z]/, t('passwordLowercase'))
            .matches(/[A-Z]/, t('passwordUppercase'))
            .matches(/[0-9]/, t('passwordNumber'))
            .matches(/[^a-zA-Z0-9]/, t('passwordSpecial'))
            .required(t('passwordRequired')),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], t('passwordsMatch'))
            .required(t('confirmPasswordRequired'))
    });

    const formik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!userId) {
                toast.current.show({
                    severity: 'error',
                    summary: t('error'),
                    detail: t('invalidResetLink'),
                    life: 5000
                });
                return;
            }

            setIsLoading(true);
            try {
                await axiosInstance.post(`/api/v1/no-auth/change-password/${userId}`, {
                    newPassword: values.newPassword,
                    confirmPassword: values.confirmPassword
                });

                toast.current.show({
                    severity: 'success',
                    summary: t('success'),
                    detail: t('passwordResetSuccess'),
                    life: 5000
                });

                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } catch (error) {
                console.error('Password reset confirm error:', error);
                toast.current.show({
                    severity: 'error',
                    summary: t('error'),
                    detail: error.response?.data?.message || t('passwordResetError'),
                    life: 5000
                });
            } finally {
                setIsLoading(false);
            }
        }
    });

    const isInvalid = name => formik.touched[name] && formik.errors[name];

    const passwordFooter = (
        <>
            <p className="mt-2 text-xs">{t('passwordRequirements')}:</p>
            <ul className="pl-2 ml-2 mt-0 text-xs">
                <li>*{t('passwordLowercase')}</li>
                <li>*{t('passwordUppercase')}</li>
                <li>*{t('passwordNumber')}</li>
                <li>*{t('passwordSpecial')}</li>
                <li>*{t('passwordMinLength')}</li>
            </ul>
        </>
    );

    // userId yoksa hata göster
    if (!userId) {
        return (
            <div className="relative h-screen w-screen overflow-hidden">
                <Toast ref={toast} />
                <div
                    className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 dark:filter dark:grayscale-[70%]"
                    style={{
                        backgroundImage: `url(${bgImage})`,
                    }}
                />
                <div className="relative flex justify-center items-center h-full">
                    <div className="bg-white bg-opacity-90 rounded-3xl p-8 shadow-2xl shadow-gray-500 max-w-md text-center">
                        <img src={logoImage} alt="Logo" className="h-16 w-16 mx-auto mb-4 object-contain" />
                        <h2 className="text-xl font-bold text-red-600 mb-4">{t('invalidResetLink')}</h2>
                        <p className="text-gray-600 mb-6">{t('resetLinkExpiredOrInvalid')}</p>
                        <Button
                            label={t('backToLogin')}
                            onClick={() => navigate('/login')}
                            className="bg-logo-mid hover:bg-logo-dark py-3 text-white font-medium rounded-lg"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-screen w-screen overflow-hidden">
            <Toast ref={toast} />

            <div
                className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 dark:filter dark:grayscale-[70%]"
                style={{
                    backgroundImage: `url(${bgImage})`,
                }}
            />

            <div className="relative flex gap-0 md:gap-10 p-5 md:p-10 h-full">
                {/* Left Section - Logo and Image */}
                <div className="hidden md:flex h-[90vh] w-[50vw] bg-white bg-opacity-50 justify-center items-center rounded-3xl">
                    <div className="h-[90%] w-[90%] flex justify-start items-center bg-white bg-opacity-80 p-2 rounded-3xl relative shadow-2xl shadow-gray-500">
                        <img
                            className='dark:filter dark:grayscale-[70%]'
                            src={logoImageWithTitle}
                            alt="Logo"
                        />
                    </div>
                </div>

                {/* Right Section - New Password Form */}
                <div className="h-[90vh] w-full md:w-[50vw] bg-white bg-opacity-50 p-3 md:p-5 flex justify-center items-center rounded-3xl">
                    <div className="bg-white bg-opacity-90 rounded-3xl p-2 shadow-2xl shadow-gray-500 h-fit w-full md:w-[90%] max-w-md">
                        <form onSubmit={formik.handleSubmit} className="flex flex-col">
                            <div className="flex justify-center py-4">
                                <img
                                    src={logoImage}
                                    alt="Logo"
                                    className="h-[8vh] md:h-[10vh] w-[12vw] md:w-[8vw] object-contain dark:filter dark:grayscale-[70%]"
                                />
                            </div>

                            <div className="text-center flex flex-col items-center gap-y-2 py-4">
                                <h1 className="font-firaSans font-bold text-xl md:text-2xl text-logo-darkest">
                                    {t('createNewPassword')}
                                </h1>
                                <p className="text-xs md:text-sm text-logo-dark text-center px-4">
                                    {t('enterNewPasswordDescription')}
                                </p>
                            </div>

                            <div className="flex flex-col gap-y-4 p-4 md:p-6">
                                <FloatLabel className="flex">
                                    <Password
                                        id="newPassword"
                                        name="newPassword"
                                        value={formik.values.newPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        toggleMask
                                        footer={passwordFooter}
                                        className={`w-full shadow-lg ${isInvalid('newPassword') ? 'p-invalid' : ''}`}
                                        inputClassName="w-full border-logo-mid dark:border-black rounded-lg p-3 md:p-4"
                                    />
                                    <label htmlFor="newPassword" className="ml-5 font-semibold text-sm md:text-base">
                                        {t('newPassword')} <span className="text-red-500">*</span>
                                    </label>
                                </FloatLabel>
                                {isInvalid('newPassword') && (
                                    <small className="p-error text-xs">{formik.errors.newPassword}</small>
                                )}

                                <FloatLabel className="flex">
                                    <Password
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formik.values.confirmPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        toggleMask
                                        feedback={false}
                                        className={`w-full shadow-lg ${isInvalid('confirmPassword') ? 'p-invalid' : ''}`}
                                        inputClassName="w-full border-logo-mid dark:border-black rounded-lg p-3 md:p-4"
                                    />
                                    <label htmlFor="confirmPassword" className="ml-5 font-semibold text-sm md:text-base">
                                        {t('confirmNewPassword')} <span className="text-red-500">*</span>
                                    </label>
                                </FloatLabel>
                                {isInvalid('confirmPassword') && (
                                    <small className="p-error text-xs">{formik.errors.confirmPassword}</small>
                                )}

                                <Button
                                    type="submit"
                                    label={t('updatePassword')}
                                    loading={isLoading}
                                    className="bg-logo-mid hover:bg-logo-dark dark:bg-gray-500 dark:hover:bg-gray-600 py-4 md:py-5 text-white font-medium rounded-lg shadow-lg mt-4"
                                />

                                <div className="flex justify-center mt-4">
                                    <p
                                        onClick={() => navigate('/login')}
                                        className="text-center text-logo-dark font-semibold hover:underline hover:text-logo-darkest text-sm md:text-base cursor-pointer"
                                    >
                                        {t('backToLogin')}
                                    </p>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .p-password-input {
                    height: 100% !important;
                    width: 100% !important;
                }
                
                .p-password-panel {
                    width: 100%;
                }
                
                :global(.dark) .p-password-panel {
                    background-color: #1e293b !important;
                    color: #e2e8f0 !important;
                    border-color: #475569 !important;
                }
                
                :global(.p-password-meter) {
                    margin-bottom: 0.75rem;
                }
                
                :global(.p-password-meter .p-password-strength.weak) {
                    background: #ef4444 !important;
                }
                
                :global(.p-password-meter .p-password-strength.medium) {
                    background: #f59e0b !important;
                }
                
                :global(.p-password-meter .p-password-strength.strong) {
                    background: #10b981 !important;
                }
                
                :global(.p-float-label) {
                    width: 100%;
                }
                
                :global(.p-float-label input:focus) ~ label,
                :global(.p-float-label input.p-filled) ~ label {
                    top: -0.75rem;
                    font-size: 12px;
                    background-color: #ffffff;
                    padding: 2px 4px;
                    border-radius: 3px;
                }
                
                :global(.dark) .p-float-label input:focus ~ label,
                :global(.dark) .p-float-label input.p-filled ~ label {
                    background-color: #2d3748;
                    color: #60a5fa;
                }
            `}</style>
        </div>
    );
};

export default ResetPasswordConfirm;